import React, { useState, useRef, useEffect } from "react";
import { CalendarRange, ChevronDown, Check } from "lucide-react";
import { useGlobalDate, RANGE_OPTIONS } from "../contexts/GlobalDateContext";

export default function Topbar({ title, subtitle, actions }) {
  const { range, label, from, to, setRange, customFrom, customTo, setCustom } = useGlobalDate();
  const [open, setOpen] = useState(false);
  const [editingCustom, setEditingCustom] = useState(false);
  const [draftFrom, setDraftFrom] = useState(customFrom || from);
  const [draftTo, setDraftTo] = useState(customTo || to);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setEditingCustom(false); } };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const choose = (key) => {
    if (key === "custom") {
      setEditingCustom(true);
      setDraftFrom(customFrom || from);
      setDraftTo(customTo || to);
      return;
    }
    setRange(key);
    setOpen(false);
  };

  const applyCustom = () => {
    setCustom(draftFrom, draftTo);
    setRange("custom");
    setEditingCustom(false);
    setOpen(false);
  };

  return (
    <div className="h-16 border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-30 flex items-center justify-between px-8" data-testid="app-topbar">
      <div>
        <div className="label-mono">Ads Intelligence CRM</div>
        <h1 className="font-heading text-lg font-medium tracking-tight leading-tight">{title}</h1>
      </div>
      <div className="flex items-center gap-3">
        {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
        <div ref={ref} className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="global-date-trigger"
            className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-border rounded-sm bg-background text-xs hover:bg-secondary transition-colors"
          >
            <CalendarRange className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span className="font-medium">{label}</span>
            <span className="text-[11px] text-muted-foreground tabular">{from} → {to}</span>
            <ChevronDown className="w-3 h-3 opacity-60" />
          </button>
          {open && (
            <div className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-sm shadow-lg z-50" data-testid="global-date-menu">
              <div className="max-h-[60vh] overflow-y-auto">
                {RANGE_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => choose(opt.key)}
                    data-testid={`global-date-opt-${opt.key}`}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-secondary text-left"
                  >
                    <span>{opt.label}</span>
                    {opt.key === range && <Check className="w-3.5 h-3.5 text-primary" />}
                  </button>
                ))}
              </div>
              {editingCustom && (
                <div className="border-t border-border p-3 space-y-2 bg-secondary/20">
                  <div className="label-mono">Custom range</div>
                  <div className="flex items-center gap-2">
                    <input type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} data-testid="global-date-from" className="flex-1 h-8 px-2 border border-border rounded-sm text-xs bg-card" />
                    <span className="text-muted-foreground text-xs">→</span>
                    <input type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} data-testid="global-date-to" className="flex-1 h-8 px-2 border border-border rounded-sm text-xs bg-card" />
                  </div>
                  <button type="button" onClick={applyCustom} data-testid="global-date-apply" className="w-full h-8 bg-primary text-primary-foreground text-xs font-semibold rounded-sm">Apply</button>
                </div>
              )}
            </div>
          )}
        </div>
        {actions}
      </div>
    </div>
  );
}
